import { computed, ref, watch, type Ref } from 'vue'

import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
  type Placement,
} from '@floating-ui/vue'

import { useEventListener } from './useEventListener'

const TRAVEL = 8
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)'
const ENTER_FADE = 100
const ENTER_TRAVEL = 130
const LEAVE = 100

export function useFloatingMenu(
  visible: Ref<boolean>,
  options: {
    flip?: boolean
    placement?: Placement
    matchWidth?: boolean
    offset?: number
  } = {}
) {
  const reference = ref<HTMLElement>()
  const floating = ref<HTMLElement>()

  const { floatingStyles, placement, isPositioned } = useFloating(
    reference,
    floating,
    {
      open: computed(() => visible.value && !!floating.value),
      placement: options.placement ?? 'bottom-start',
      whileElementsMounted: autoUpdate,
      transform: false,
      middleware: [
        offset(options.offset ?? 3),
        ...(options.flip === false ? [] : [flip({ padding: 8 })]),
        shift({ padding: 8 }),
        ...(options.matchWidth === false
          ? []
          : [
              size({
                padding: 8,
                apply({ rects, elements }) {
                  elements.floating.style.width = `${rects.reference.width}px`
                },
              }),
            ]),
      ],
    }
  )

  useEventListener(window, 'click', (event) => {
    const target = event.target as Node

    if (reference.value?.contains(target) || floating.value?.contains(target)) {
      return
    }

    visible.value = false
  })

  const travel = () => (placement.value.startsWith('top') ? TRAVEL : -TRAVEL)

  let pendingEnter: (() => void) | null = null

  const flushPendingEnter = () => {
    const start = pendingEnter
    pendingEnter = null
    start?.()
  }

  watch(isPositioned, (positioned) => positioned && flushPendingEnter(), {
    flush: 'sync',
  })

  const onBeforeEnter = (el: Element) => {
    ;(el as HTMLElement).style.opacity = '0'
  }

  const onEnter = (el: Element, done: () => void) => {
    const start = () => {
      el.getAnimations().forEach((animation) => animation.cancel())
      ;(el as HTMLElement).style.opacity = ''

      el.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: ENTER_FADE,
        fill: 'backwards',
      })

      const travelling = el.animate(
        [{ transform: `translateY(${travel()}px)` }, { transform: 'none' }],
        { duration: ENTER_TRAVEL, easing: EASING, fill: 'backwards' }
      )

      travelling.onfinish = done
      travelling.oncancel = done
    }

    if (isPositioned.value) return start()

    pendingEnter = start
    requestAnimationFrame(() =>
      requestAnimationFrame(() => pendingEnter === start && flushPendingEnter())
    )
  }

  const onEnterCancelled = (el: Element) => {
    pendingEnter = null
    ;(el as HTMLElement).style.opacity = ''
  }

  const onLeave = (el: Element, done: () => void) => {
    el.getAnimations().forEach((animation) => animation.cancel())

    el.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: LEAVE,
      fill: 'forwards',
    })

    const travelling = el.animate(
      [{ transform: 'none' }, { transform: `translateY(${travel()}px)` }],
      { duration: LEAVE, easing: EASING, fill: 'forwards' }
    )

    travelling.onfinish = done
    travelling.oncancel = done
  }

  const transition = {
    css: false,
    onBeforeEnter,
    onEnter,
    onEnterCancelled,
    onLeave,
  }

  return { reference, floating, floatingStyles, placement, transition }
}
